import {
    getCommandClient,
    getVideoClient,
    getVideoParticipants,
    onVideoSessionReady,
    onVideoSessionClosed,
    onVideoParticipantsChange,
    onActiveShareChange,
    setLocalShareFrozen,
} from './useVideoSession';
import { getLiveClassState, patchLiveClassState } from './liveClassStore';
import { logLiveClassEvent } from '../utils/liveClassLogger';
import {
    canFreezeScreen,
    canManageParticipants,
    permissionError,
} from '../utils/liveClassPermissions';

/**
 * Señales de la clase que el Video SDK no expone como API propia
 * (mano levantada y congelar pantalla) viajan por el command channel.
 *
 * El canal no guarda historial: quien tenga un estado activo lo reenvía a cada
 * usuario que entra (`user-added`) para que los rezagados no queden desfasados.
 */
const SIGNAL = {
    HAND: 'hand',
    HAND_CLEAR: 'hand-clear',
    FREEZE: 'freeze',
};

const toUserId = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const send = async (payload, userId = undefined) => {
    const commandClient = getCommandClient();
    if (!commandClient) return false;
    try {
        await commandClient.send(JSON.stringify(payload), userId);
        return true;
    } catch (err) {
        logLiveClassEvent('signal-send-failed', {
            reason: err?.reason || err?.message || String(err),
            detail: payload?.t,
        });
        return false;
    }
};

const isHostUser = (userId) => {
    const participant = getVideoParticipants().find((p) => p.userId === userId);
    return Boolean(participant?.isHost || participant?.isManager);
};

const addHand = (userId) => {
    const current = getLiveClassState().raisedHandUserIds;
    if (current.includes(userId)) return;
    patchLiveClassState({ raisedHandUserIds: [...current, userId] });
};

const removeHand = (userId) => {
    const current = getLiveClassState().raisedHandUserIds;
    if (!current.includes(userId)) return;
    patchLiveClassState({ raisedHandUserIds: current.filter((id) => id !== userId) });
};

const onCommandMessage = (payload) => {
    const senderId = toUserId(payload?.senderId);
    if (senderId == null) return;

    let message = null;
    try {
        message = JSON.parse(payload.text);
    } catch {
        return;
    }
    if (!message || typeof message !== 'object') return;

    if (message.t === SIGNAL.HAND) {
        if (message.on) addHand(senderId);
        else removeHand(senderId);
        return;
    }

    if (message.t === SIGNAL.HAND_CLEAR) {
        // Solo el anfitrión puede bajar la mano de alguien más.
        if (!isHostUser(senderId)) return;
        const target = toUserId(message.u);
        if (target != null) removeHand(target);
        return;
    }

    if (message.t === SIGNAL.FREEZE) {
        if (!isHostUser(senderId)) return;
        logLiveClassEvent('freeze-received', { detail: { on: Boolean(message.on) } });
        patchLiveClassState({ screenFrozen: Boolean(message.on) });
    }
};

const onCommandStatus = (state) => {
    logLiveClassEvent('command-channel-status', { detail: { state } });
};

/** Un usuario nuevo no recibió los mensajes previos: se le reenvía el estado. */
const onUserAdded = (payload) => {
    const list = Array.isArray(payload) ? payload : [payload];
    const state = getLiveClassState();
    const selfId = state.selfUserId;

    list.forEach((entry) => {
        const userId = toUserId(entry?.userId);
        if (userId == null || userId === selfId) return;

        if (state.isHost && state.screenFrozen) {
            void send({ t: SIGNAL.FREEZE, on: true }, userId);
        }
        if (selfId != null && state.raisedHandUserIds.includes(selfId)) {
            void send({ t: SIGNAL.HAND, on: true }, userId);
        }
    });
};

const attach = (client) => {
    if (!client) return;
    try {
        client.on('command-channel-message', onCommandMessage);
        client.on('command-channel-status', onCommandStatus);
        client.on('user-added', onUserAdded);
    } catch (err) {
        console.warn('[LiveClass] command channel:', err?.message || err);
    }
};

const detach = (client) => {
    if (!client) return;
    try {
        client.off('command-channel-message', onCommandMessage);
        client.off('command-channel-status', onCommandStatus);
        client.off('user-added', onUserAdded);
    } catch {
        // ignore
    }
};

let attachedClient = null;

onVideoSessionReady((client) => {
    if (attachedClient && attachedClient !== client) {
        detach(attachedClient);
        attachedClient = null;
    }
    if (client && attachedClient !== client) {
        attach(client);
        attachedClient = client;
    }
});

onVideoSessionClosed(() => {
    detach(attachedClient);
    attachedClient = null;
    patchLiveClassState({ screenFrozen: false, raisedHandUserIds: [] });
});

// Sin share activo no hay nada congelado: evita que el aviso quede pegado en los alumnos.
onActiveShareChange((shareUserId) => {
    if (shareUserId == null && getLiveClassState().screenFrozen) {
        patchLiveClassState({ screenFrozen: false });
    }
});

// Si alguien sale de la clase, su mano levantada deja de tener sentido.
onVideoParticipantsChange((participants) => {
    const { raisedHandUserIds } = getLiveClassState();
    if (!raisedHandUserIds.length) return;
    const present = new Set(participants.map((p) => p.userId));
    const next = raisedHandUserIds.filter((id) => present.has(id));
    if (next.length !== raisedHandUserIds.length) {
        patchLiveClassState({ raisedHandUserIds: next });
    }
});

/** Levanta o baja la propia mano y lo anuncia al resto. */
export const setOwnHandRaised = async (raised) => {
    const selfId = getLiveClassState().selfUserId
        ?? getVideoClient()?.getCurrentUserInfo?.()?.userId
        ?? null;
    if (selfId == null) return;

    if (raised) addHand(selfId);
    else removeHand(selfId);

    const sent = await send({ t: SIGNAL.HAND, on: Boolean(raised) });
    if (!sent) {
        // Sin canal no hay forma de avisar: se revierte para no mentir en la UI.
        if (raised) removeHand(selfId);
        else addHand(selfId);
        throw new Error('No se pudo avisar al profesor. Intenta de nuevo.');
    }
    logLiveClassEvent('hand-raised', { detail: { on: Boolean(raised) } });
};

/** El anfitrión baja la mano de un alumno. */
export const clearParticipantHand = async (userId) => {
    if (!canManageParticipants(getLiveClassState())) {
        throw permissionError('bajar la mano de otros');
    }
    const target = toUserId(userId);
    if (target == null) return;
    removeHand(target);
    await send({ t: SIGNAL.HAND_CLEAR, u: target });
};

/**
 * Congela o reanuda la pantalla compartida del profesor.
 * El congelado real lo hace Zoom (pausa del share); la señal solo sirve para que
 * los alumnos vean un aviso, así que si no llega el congelado igual funciona.
 */
export const setScreenFrozen = async (frozen) => {
    if (!canFreezeScreen(getLiveClassState())) {
        throw permissionError('congelar la pantalla');
    }
    await setLocalShareFrozen(Boolean(frozen));
    const sent = await send({ t: SIGNAL.FREEZE, on: Boolean(frozen) });
    logLiveClassEvent('freeze-sent', { detail: { on: Boolean(frozen), notified: sent } });
};
